import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Badge, Nav, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import CouponsManager from "./modules/coupons/CouponsManager";
import PriceListsManager from "./modules/prices/PriceListsManager";
import DynamicPricingManager from "./modules/prices/DynamicPricingManager";
import AddonsManager from "./modules/prices/AddonsManager";
import RefreshmentRoomsManager from "./modules/refreshments/RefreshmentRoomsManager";
import HotelsManager from "./modules/hotels/HotelsManager";
import TripsManager from "./modules/trips/TripsManager";
import BookingsManager from "./modules/bookings/BookingsManager";
import TransportManager from "./modules/transport/TransportManager";
import LeadTrackerManager from "./modules/leads/LeadTrackerManager";
import ExternalVendorsManager from "./modules/vendors/ExternalVendorsManager";
import ExpensesManager from "./modules/expenses/ExpensesManager";
import PayrollManager from "./modules/expenses/PayrollManager";
import FinanceDashboard from "./modules/finance/FinanceDashboard";
import IncentivesManager from "./modules/incentives/IncentivesManager";
import CompanyPaymentDetailsManager from "./modules/settings/CompanyPaymentDetailsManager";
import CustomerPolicyManager from "./modules/settings/CustomerPolicyManager";
import DriverGuideVideosManager from "./modules/settings/DriverGuideVideosManager";
import EpassesManager from "./modules/epasses/EpassesManager";
import EmergencyContactsManager from "./modules/settings/EmergencyContactsManager";
import TrackingSettingsManager from "./modules/settings/TrackingSettingsManager";
import StaffThemeSwitch, { useStaffTheme } from "../components/StaffThemeSwitch";
import { getBackendVersion } from "../services/versionService";
import {
  BACKEND_REPO_URL,
  FRONTEND_BUILT_AT,
  FRONTEND_COMMIT,
  FRONTEND_REPO_URL,
  IS_DEV_SERVER,
} from "../config/buildInfo";

const activeModules = [
  "coupons",
  "trips",
  "dynamic-pricing",
  "hotels",
  "refreshment-rooms",
  "addons",
  "epasses",
  "bookings",
  "transport",
  "lead-tracker",
  "external-vendors",
  "expenses",
  "finance",
  "incentives",
  "payment-details",
  "customer-policy",
  "emergency-contacts",
  "driver-guide-videos",
  "tracking-settings",
];

const isMobileViewport = () => typeof window !== "undefined" && window.innerWidth < 768;

export default function AdminDashboard() {
  // Nothing loads until a section is picked in Navigation.
  const [activeKey, setActiveKey] = useState(null);
  // Bumped when payroll writes to the ledger, so the expense list above it reloads.
  const [ledgerVersion, setLedgerVersion] = useState(0);
  const [theme, setTheme] = useStaffTheme();
  const [navOpen, setNavOpen] = useState(() => {
    const stored = localStorage.getItem("adminNavOpen");
    return stored === null ? !isMobileViewport() : stored !== "false";
  });

  const toggleNav = () => {
    setNavOpen((current) => {
      const next = !current;
      localStorage.setItem("adminNavOpen", String(next));
      return next;
    });
  };

  const handleSelect = (key) => {
    setActiveKey(key);
    if (isMobileViewport()) setNavOpen(false);
  };

  return (
    <Container fluid className="py-3">
      <Header navOpen={navOpen} onToggleNav={toggleNav} theme={theme} onThemeChange={setTheme} />

      <Row className="g-3">
        {navOpen && (
          <Col xs={12} md={3} lg={2}>
            <LeftNav activeKey={activeKey} onSelect={handleSelect} onClose={toggleNav} />
          </Col>
        )}

        <Col xs={12} md={navOpen ? 9 : 12} lg={navOpen ? 10 : 12}>
          {!activeKey && <ChooseSection navOpen={navOpen} onShowNav={toggleNav} />}
          {activeKey === "coupons" && <CouponsManager />}
          {activeKey === "trips" && <TripsManager />}
          {activeKey === "prices" && <PriceListsManager />}
          {activeKey === "dynamic-pricing" && <DynamicPricingManager />}
          {activeKey === "hotels" && <HotelsManager />}
          {activeKey === "refreshment-rooms" && <RefreshmentRoomsManager />}
          {activeKey === "addons" && <AddonsManager />}
          {activeKey === "epasses" && <EpassesManager />}
          {activeKey === "bookings" && <BookingsManager />}
          {activeKey === "transport" && <TransportManager />}
          {activeKey === "lead-tracker" && <LeadTrackerManager />}
          {activeKey === "external-vendors" && <ExternalVendorsManager />}
          {activeKey === "expenses" && (
            <>
              <ExpensesManager key={ledgerVersion} />
              <PayrollManager onLedgerChanged={() => setLedgerVersion((value) => value + 1)} />
            </>
          )}
          {activeKey === "finance" && <FinanceDashboard />}
          {activeKey === "incentives" && <IncentivesManager />}
          {activeKey === "payment-details" && <CompanyPaymentDetailsManager />}
          {activeKey === "customer-policy" && <CustomerPolicyManager />}
          {activeKey === "emergency-contacts" && <EmergencyContactsManager />}
          {activeKey === "driver-guide-videos" && <DriverGuideVideosManager />}
          {activeKey === "tracking-settings" && <TrackingSettingsManager />}
          {activeKey && !activeModules.includes(activeKey) && <ComingSoon label={activeKey} />}
        </Col>
      </Row>

      <Footer />
    </Container>
  );
}

function Header({ navOpen, onToggleNav, theme, onThemeChange }) {
  return (
    <Row className="align-items-center mb-2">
      <Col>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <Button size="sm" variant="outline-secondary" onClick={onToggleNav}>
            {navOpen ? "Hide Navigation" : "Show Navigation"}
          </Button>
          <h4 className="mb-0">Admin Panel</h4>
        </div>
        <div className="text-muted small">
          Manage content, pricing, and operations
        </div>
      </Col>
      <Col xs="auto" className="d-flex align-items-center gap-2">
        <StaffThemeSwitch theme={theme} onChange={onThemeChange} size="sm" />
        <Badge bg="info" className="d-none d-md-inline-block">Admin</Badge>
      </Col>
    </Row>
  );
}

function LeftNav({ activeKey, onSelect, onClose }) {
  const navigate = useNavigate();

  return (
    <Card className="shadow-sm">
      <Card.Header className="d-flex align-items-center justify-content-between gap-2">
        <span className="fw-semibold">Navigation</span>
        <Button size="sm" variant="outline-secondary" onClick={onClose}>Close</Button>
      </Card.Header>
      <Nav
        variant="pills"
        className="flex-column p-2"
        activeKey={activeKey}
        onSelect={(key) => {
          if (key === "hotel-admin") {
            navigate("/hoteladmin");
            return;
          }
          onSelect(key || "trips");
        }}
      >
        <Nav.Item>
          <Nav.Link eventKey="trips">Trips</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="dynamic-pricing">Dynamic Pricing</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="hotels">Hotel Master</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="hotel-admin">Channel Manager</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="transport">Transport</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="lead-tracker">Lead Tracker</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="bookings">Bookings</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="expenses">Expenses</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="finance">Profit &amp; ROI</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="incentives">Incentives</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="coupons">Manage Coupons</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="refreshment-rooms">Refreshment Rooms</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="addons">Add-ons</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="epasses">E-Passes</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="external-vendors">External Vendor</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="payment-details">Payment Details</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="customer-policy">Customer Trip Policy</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="emergency-contacts">Emergency Contacts</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="driver-guide-videos">Driver Guide Videos</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="tracking-settings">Tracking Settings</Nav.Link>
        </Nav.Item>
      </Nav>
    </Card>
  );
}

function ChooseSection({ navOpen, onShowNav }) {
  return (
    <Card className="shadow-sm">
      <Card.Body className="text-center py-5">
        <div className="h5 mb-1">Choose a section</div>
        <div className="text-muted">Pick a menu in Navigation to load it.</div>
        {!navOpen && (
          <Button size="sm" className="mt-3" onClick={onShowNav}>
            Show Navigation
          </Button>
        )}
      </Card.Body>
    </Card>
  );
}

function ComingSoon({ label }) {
  return (
    <Card className="shadow-sm">
      <Card.Body className="text-center py-5">
        <div className="h5 mb-1 text-capitalize">{label} coming soon</div>
        <div className="text-muted">This section is under construction.</div>
      </Card.Body>
    </Card>
  );
}

function Footer() {
  return (
    <Row className="mt-3">
      <Col>
        <div className="text-center text-muted small">
          Indian Odyssey Admin
        </div>
        <VersionLine />
      </Col>
    </Row>
  );
}

// Which builds are running here, so a push can be checked live without opening GitHub. On localhost it
// shows the local frontend and whichever backend npm start points at.
function VersionLine() {
  const [server, setServer] = useState(null);
  const [serverFailed, setServerFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getBackendVersion()
      .then((info) => !cancelled && setServer(info))
      .catch(() => !cancelled && setServerFailed(true));
    return () => {
      cancelled = true;
    };
  }, []);

  let website;
  if (FRONTEND_COMMIT) website = <Build repo={FRONTEND_REPO_URL} commit={FRONTEND_COMMIT} builtAt={FRONTEND_BUILT_AT} />;
  else website = IS_DEV_SERVER ? "local (npm start)" : "hand-made build";

  let backend;
  if (serverFailed) backend = "unknown";
  else if (!server) backend = "…";
  else {
    const local = server.environment === "local";
    backend = (
      <>
        {local && "local · "}
        {server.commit ? (
          <Build
            repo={BACKEND_REPO_URL}
            commit={server.commit}
            builtAt={server.builtAt}
            uncommitted={server.uncommittedChanges}
          />
        ) : (
          "no build stamp"
        )}
      </>
    );
  }

  return (
    <div className="text-center text-muted small">
      Website {website} | Server {backend}
    </div>
  );
}

function Build({ repo, commit, builtAt, uncommitted }) {
  const short = commit.slice(0, 7);
  return (
    <>
      <a href={`${repo}/commit/${commit}`} target="_blank" rel="noreferrer" className="text-reset">
        {short}
      </a>
      {uncommitted && " + uncommitted changes"}
      {builtAt && ` · built ${formatBuiltAt(builtAt)}`}
    </>
  );
}

function formatBuiltAt(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });
}
